import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { DateRange } from '../../../components/shared/filters/DateRangePicker';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { conversationOriginLabel, type ContactEmbed } from '../../../services/modules/aiChatContext';

const PAGE_SIZE = 1000;
const MAX_ROWS = 5000;

type ConversationQueryRow = {
    id: string;
    assigned_to: string | null;
    meta_attribution: unknown;
    contact: ContactEmbed;
};

type ConversationsByChannel = {
    name: string;
    conversas: number;
};

function countByOrigin(
    rows: { origin: string; assignedTo: string | null }[],
    canViewAll: boolean,
    userId?: string,
): ConversationsByChannel[] {
    const visible = canViewAll
        ? rows
        : rows.filter((row) => !row.assignedTo || row.assignedTo === userId);

    const totals = new Map<string, number>();
    visible.forEach((row) => {
        const name = row.origin.trim() || 'Sem origem';
        totals.set(name, (totals.get(name) ?? 0) + 1);
    });

    return Array.from(totals.entries())
        .map(([name, conversas]) => ({ name, conversas }))
        .sort((a, b) => b.conversas - a.conversas || a.name.localeCompare(b.name, 'pt-BR'));
}

const CONVERSATION_FIELDS = `
    id,
    assigned_to,
    meta_attribution,
    contact:contacts (
        origins ( name )
    )
`;

async function fetchMatching(
    organizationId: string,
    dateRange: DateRange,
    by: 'created' | 'captured',
): Promise<ConversationQueryRow[]> {
    const rows: ConversationQueryRow[] = [];
    const start = dateRange.start?.toISOString();
    const end = dateRange.end?.toISOString();
    const column = by === 'created' ? 'created_at' : 'meta_attribution->>captured_at';

    for (let from = 0; from < MAX_ROWS; from += PAGE_SIZE) {
        let query = supabase
            .from('chat_conversations')
            .select(CONVERSATION_FIELDS)
            .eq('organization_id', organizationId);

        if (start && end) {
            query = query.gte(column, start).lte(column, end);
        }

        const { data, error } = await query
            .order('created_at', { ascending: false })
            .range(from, from + PAGE_SIZE - 1);
        if (error) throw error;

        const page = (data ?? []) as ConversationQueryRow[];
        rows.push(...page);
        if (page.length < PAGE_SIZE) break;
    }

    return rows;
}

/**
 * Uma conversa entra no período se nasceu nele ou se o anúncio foi capturado nele.
 * Chat antigo que recebe um clique de anúncio no mês não pode ficar de fora.
 */
async function fetchConversationRows(
    organizationId: string,
    dateRange: DateRange,
): Promise<{ origin: string; assignedTo: string | null }[]> {
    const createdRows = await fetchMatching(organizationId, dateRange, 'created');
    const capturedRows = dateRange.start && dateRange.end
        ? await fetchMatching(organizationId, dateRange, 'captured')
        : [];

    const byId = new Map<string, { origin: string; assignedTo: string | null }>();
    [...createdRows, ...capturedRows].forEach((row) => {
        byId.set(row.id, {
            origin: conversationOriginLabel(row.contact, row.meta_attribution),
            assignedTo: row.assigned_to,
        });
    });

    return Array.from(byId.values());
}

export const useConversationsByChannel = (dateRange: DateRange, enabled: boolean) => {
    const { currentOrganization } = useOrganization();
    const { user } = useAuth();
    const { canViewAllConversations } = usePermissions();
    const [data, setData] = useState<ConversationsByChannel[]>([]);
    const [isLoading, setIsLoading] = useState(enabled);
    const requestIdRef = useRef(0);

    const startIso = dateRange.start?.toISOString() ?? '';
    const endIso = dateRange.end?.toISOString() ?? '';

    useEffect(() => {
        if (!enabled || !currentOrganization?.id) {
            requestIdRef.current += 1;
            setData([]);
            setIsLoading(false);
            return;
        }

        const organizationId = currentOrganization.id;
        const requestId = ++requestIdRef.current;
        const range: DateRange = {
            start: startIso ? new Date(startIso) : null,
            end: endIso ? new Date(endIso) : null,
        };
        setIsLoading(true);

        const load = async () => {
            try {
                const rows = await fetchConversationRows(organizationId, range);
                if (requestId !== requestIdRef.current) return;
                setData(countByOrigin(rows, canViewAllConversations, user?.id));
            } catch (error) {
                if (requestId !== requestIdRef.current) return;
                console.error('[useConversationsByChannel] Failed to load:', error);
                setData([]);
            } finally {
                if (requestId === requestIdRef.current) setIsLoading(false);
            }
        };

        void load();
    }, [enabled, currentOrganization?.id, startIso, endIso, canViewAllConversations, user?.id]);

    return { conversationsByChannel: data, isLoadingChannels: isLoading };
};
